import { configure, BarcodePicker, ScanSettings, Barcode } from 'scandit-sdk';
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import __vue_component__$1 from '../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__ from '../../cards/BasicCard/BasicCard.vue.js';
import __vue_component__$4 from '../../inputs/TextInput/TextInput.vue.js';
import __vue_component__$3 from './components/ManualEntryAlert.vue.js';
import TransitionExpand from '../../transitions/TransitionExpand/TransitionExpand.js';
import __vue_component__$2 from '../../ui/Loading/Loading.vue.js';
import { getScanditAPIKey, SCANDIT_BUILD_SDK } from '../../../database/firebaseConfig/credentials.js';

var script = defineComponent({
    name: 'BarcodeReader',
    components: {
        BasicCard: __vue_component__,
        ButtonComponent: __vue_component__$1,
        Loading: __vue_component__$2,
        ManualEntryAlert: __vue_component__$3,
        TextInput: __vue_component__$4,
        TransitionExpand,
    },
    props: {
        enableMultipleScans: Boolean,
        inlineManualEntry: Boolean,
        enableManualEntry: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { emit }) {
        const isLoading = ref(true);
        const barcodePicker = ref(null);
        const barcodePickerElement = ref(null);
        const errorMessage = ref(null);
        const isManualEntryAlertOpened = ref(false);
        const manualEntryAlertRef = ref(null);
        const readerClassNames = computed(() => {
            const names = [];
            if (isLoading.value || errorMessage.value) {
                names.push('BarcodeReader__reader--with-background');
            }
            return names;
        });
        onMounted(async () => {
            try {
                await configure(getScanditAPIKey(), {
                    engineLocation: SCANDIT_BUILD_SDK,
                });
                if (!barcodePickerElement.value)
                    return;
                barcodePicker.value = await BarcodePicker.create(barcodePickerElement.value, {
                    playSoundOnScan: true,
                    videoFit: BarcodePicker.ObjectFit.COVER,
                });
                const scanSettings = new ScanSettings({
                    enabledSymbologies: [
                        Barcode.Symbology.MICRO_PDF417,
                        Barcode.Symbology.DATA_MATRIX,
                        Barcode.Symbology.CODE128,
                    ],
                    codeDuplicateFilter: 10000, // Minimum delay between duplicate results
                });
                barcodePicker.value.applyScanSettings(scanSettings);
                barcodePicker.value.on('ready', () => {
                    isLoading.value = false;
                    errorMessage.value = null;
                });
                barcodePicker.value.on('cameraAccessError', (error) => {
                    throw error;
                });
                barcodePicker.value.on('scan', async (scanResult) => {
                    var _a;
                    const { barcodes } = scanResult;
                    if (barcodes) {
                        let barcodeValue = (_a = barcodes[0]) === null || _a === void 0 ? void 0 : _a.data;
                        if (props.enableMultipleScans) {
                            barcodeValue = barcodes.map((barcode) => barcode.data);
                        }
                        console.info('onDecode', barcodeValue);
                        await new Promise((resolve) => {
                            emit('onDecode', { resolve, barcode: barcodeValue });
                        });
                    }
                });
            }
            catch (error) {
                console.error(error);
                isLoading.value = false;
                errorMessage.value
                    = 'There was an error accessing your camera. Please check your browser permissions.';
            }
        });
        onBeforeUnmount(() => {
            // Destroy BarcodePicker to ensure the "camera on" indicator turns off
            // when this component is unmounted.
            if (barcodePicker.value) {
                // If BarcodePicker ready, destroy immediately.
                barcodePicker.value.destroy(true);
            }
            else {
                // If BarcodePicker still initializing, check once per second if
                // ready to destroy.
                let intervalCount = 0;
                const interval = setInterval(() => {
                    intervalCount += 1;
                    if (intervalCount >= 15) {
                        // Give up after 15 seconds.
                        clearInterval(interval);
                    }
                    else if (barcodePicker.value) {
                        barcodePicker.value.destroy(true);
                        clearInterval(interval);
                    }
                }, 1000);
            }
        });
        const openCloseManualEntryAlert = (value) => {
            isManualEntryAlertOpened.value = value;
        };
        const confirmManualEntry = async (barcode) => {
            var _a;
            openCloseManualEntryAlert(false);
            const successDecoding = await new Promise((resolve) => {
                emit('onDecode', { resolve, barcode });
            });
            if (!successDecoding) {
                openCloseManualEntryAlert(true);
                return;
            }
            (_a = manualEntryAlertRef.value) === null || _a === void 0 ? void 0 : _a.resetBarcodeValue();
        };
        const inlineBarcodeValue = ref(undefined);
        return {
            barcodePickerElement,
            confirmManualEntry,
            errorMessage,
            inlineBarcodeValue,
            isLoading,
            isManualEntryAlertOpened,
            manualEntryAlertRef,
            openCloseManualEntryAlert,
            readerClassNames,
        };
    },
});

export { script as default };
